import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import AotdShiftBanner from "../AotdShiftBanner";
import AotdArtCard from "../AotdArtCard";
import EmojiPenaltyNote from "../shared/EmojiPenaltyNote";
import AotdCardsReadyButton from "../../../respectCards/aotd/AotdCardsReadyButton";
import { useEarlyEntryGuard } from "../../hooks/useEarlyEntryGuard";
import type { ArtInfo, EmojiPenaltyResponse } from "../../api/types";

const SignupMessage = styled.div`
  font-size: 1rem;
  font-weight: 900;
  color: #323232;
  text-align: center;
  margin-top: 2rem;
`;

/**
 * `not_started` + scheduledTime branch: shows the orange shift banner and
 * the previous AOTD as a read-only art card (if available), or a small
 * "check back later" message. After the scheduled time passes, switches
 * to a "Generate Art" CTA — unless the early-entry guard says the page
 * was pre-loaded too close to unlock. Mirrors monolith lines 1339–1526.
 */
export interface NotStartedScheduledStateProps {
  scheduledTime: string;
  previousArt?: ArtInfo;
  artUrl: string | null;
  streakIcon: URL | null;
  isAuthenticated: boolean;
  currentUsername: string | null;
  onRetry: () => void;
  onTriggerWayback: () => void | Promise<void>;
  /** The viewer's no-emoji penalty: warned about before they tap. */
  emojiPenalty?: EmojiPenaltyResponse;
}

export const NotStartedScheduledState: React.FC<NotStartedScheduledStateProps> = ({
  scheduledTime,
  previousArt,
  artUrl,
  streakIcon,
  isAuthenticated,
  currentUsername,
  onTriggerWayback,
  emojiPenalty,
}) => {
  const scheduledDate = new Date(scheduledTime);
  const now = new Date();
  const isBeforeScheduledTime = now < scheduledDate;
  const enteredTooEarly = useEarlyEntryGuard(scheduledTime);

  if (!isBeforeScheduledTime) {
    if (enteredTooEarly) {
      return (
        <SignupMessage
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <div>You loaded this page just before unlock. Please refresh your browser to continue.</div>
        </SignupMessage>
      );
    }
    if (!isAuthenticated) {
      // Signed-out visitors can't race. The page follows the day on its own
      // (useAotdRace re-reads /today on race activity), so the art shows up
      // here once someone claims it.
      return (
        <SignupMessage
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <div>It&apos;s unlocked. Sign in to race for today&apos;s art.</div>
          <div style={{ marginTop: "8px", fontSize: "0.9rem" }}>
            <Link to="/login" style={{ marginRight: "10px" }}>
              Log In
            </Link>
            <Link to="/register">Sign Up</Link>
          </div>
          <div style={{ marginTop: "10px", fontSize: "0.8rem", fontWeight: 400, color: "#666" }}>
            Or stay here: you&apos;ll see it painted as soon as someone claims it.
          </div>
        </SignupMessage>
      );
    }
    return (
      <SignupMessage
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <div>
          The scheduled time has passed — hit the refresh button to load today&apos;s art.
        </div>
        <EmojiPenaltyNote penalty={emojiPenalty} moment="upcoming" />
      </SignupMessage>
    );
  }

  return (
    <div className="art-of-the-day">
      <AotdShiftBanner scheduledTime={scheduledTime} />
      <EmojiPenaltyNote penalty={emojiPenalty} moment="upcoming" />
      <AotdCardsReadyButton />
      {artUrl && previousArt ? (
        <AotdArtCard
          artInfo={previousArt}
          artUrl={artUrl}
          streakIcon={streakIcon}
          hasPermission={false}
          isAuthenticated={isAuthenticated}
          currentUsername={currentUsername}
          onTriggerWayback={onTriggerWayback}
          hideNextEmojis
          readOnly
        />
      ) : (
        <SignupMessage style={{ textAlign: "center", marginTop: "1rem" }}>
          <div style={{ fontSize: "0.85rem", color: "#666" }}>
            Check back later to create today&apos;s art.
          </div>
        </SignupMessage>
      )}
    </div>
  );
};

export default NotStartedScheduledState;
